/**
 * Полноэкранный просмотрщик изображений (лайтбокс в духе Telegram):
 * тёмный оверлей, стрелки ←/→, Esc / клик по фону — закрыть,
 * подпись с именем файла и счётчиком, кнопка скачивания.
 *
 * @use просмотр вложения во весь экран: лайтбокс со стрелками, Esc, подписью и скачиванием.
 */

import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import styles from './MediaViewer.module.css';

export interface MediaViewerItem {
  url: string;
  fileName: string;
  type?: 'image' | 'video';
}

export interface MediaViewerProps {
  items: MediaViewerItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

export function MediaViewer({ items, index, onIndexChange, onClose }: MediaViewerProps) {
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const goPrev = useCallback(() => {
    if (index > 0) onIndexChange(index - 1);
  }, [index, onIndexChange]);

  const goNext = useCallback(() => {
    if (index < items.length - 1) onIndexChange(index + 1);
  }, [index, items.length, onIndexChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [goPrev, goNext, onClose]);

  if (!item) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.topBar} onClick={(e) => e.stopPropagation()}>
        <span className={styles.caption}>
          {item.fileName}
          {items.length > 1 && (
            <span className={styles.counter}>
              {index + 1} / {items.length}
            </span>
          )}
        </span>
        <div className={styles.actions}>
          <a
            className={styles.iconBtn}
            href={item.url}
            download={item.fileName}
            target="_blank"
            rel="noreferrer"
            title="Скачать"
          >
            <Download size={20} />
          </a>
          <button type="button" className={styles.iconBtn} onClick={onClose} title="Закрыть (Esc)">
            <X size={20} />
          </button>
        </div>
      </div>

      {hasPrev && (
        <button
          type="button"
          className={cn(styles.navBtn, styles.navPrev)}
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          title="Предыдущее (←)"
        >
          <ChevronLeft size={32} />
        </button>
      )}

      {item.type === 'video' ? (
        <video
          className={styles.image}
          src={item.url}
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          className={styles.image}
          src={item.url}
          alt={item.fileName}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {hasNext && (
        <button
          type="button"
          className={cn(styles.navBtn, styles.navNext)}
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          title="Следующее (→)"
        >
          <ChevronRight size={32} />
        </button>
      )}
    </div>,
    document.body,
  );
}
